import { useMemo } from 'react'
import { ColumnFiltersState, OnChangeFn, PaginationState } from '@tanstack/react-table'
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { ListTypedWoolLotsResponse, TypedWoolLot } from '@loom-api-types'

// TODO: This is not a sustainable solution, all GraphQL response attributes
// should be optional.
type TypedWoolLotTableData = Pick<
  TypedWoolLot,
  | 'purchaseBrand'
  | 'stationRecordNumber'
  | 'saleNumber'
  | 'season'
  | 'code'
  | 'bales'
  | 'internalReferenceCode'
  | 'typeCurrent'
  | 'internalComment'
  | 'actions'
  | 'lastTyped'
>
type TypedWoolLotTableResponse = Omit<ListTypedWoolLotsResponse, 'results'> & {
  results: Array<TypedWoolLotTableData>
}

export function TypedLotsTable({
  data,
  isError,
  isFetching,
  isLoading,
  pagination,
  setPagination,
  columnFilters,
  setColumnFilters,
}: {
  data: TypedWoolLotTableResponse | undefined
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  pagination: PaginationState
  setPagination: OnChangeFn<PaginationState>
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) {
  const columns = useMemo<MRT_ColumnDef<TypedWoolLotTableData>[]>(
    () => [
      {
        id: 'purchaseBrand',
        accessorKey: 'purchaseBrand',
        header: 'Brand',
        enableColumnFilter: false,
      },
      {
        id: 'stationRecordNumber',
        accessorKey: 'stationRecordNumber',
        header: 'P Org',
        enableColumnFilter: false,
      },
      {
        id: 'saleNumber',
        accessorKey: 'saleNumber',
        header: 'Sale',
        enableColumnFilter: false,
      },
      {
        id: 'season',
        accessorKey: 'season',
        header: 'Season',
        enableColumnFilter: false,
      },
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Lot',
        enableColumnFilter: false,
      },
      {
        id: 'bales',
        accessorKey: 'bales',
        header: 'Bales',
        enableColumnFilter: false,
      },
      {
        id: 'internalReferenceCode',
        accessorKey: 'internalReferenceCode',
        header: 'Int Ref',
        enableColumnFilter: false,
      },
      {
        id: 'typeCurrent',
        accessorKey: 'typeCurrent',
        header: 'Type',
        enableColumnFilter: false,
      },
      {
        id: 'internalComment',
        accessorKey: 'internalComment',
        header: 'Comment',
        enableColumnFilter: false,
      },
      {
        id: 'actions',
        accessorKey: 'actions',
        header: 'Actions',
        enableColumnFilter: false,
      },
      {
        id: 'lastTyped',
        header: 'To Talman',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        enableColumnFilter: true,
        accessorFn: (row) => (row.lastTyped == null ? null : new Date(row.lastTyped)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY hh:mm A'),
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data: data?.results ?? [],
    initialState: {
      ...tableDefaultProps.initialState,
      showColumnFilters: true,
    },
    state: {
      isLoading,
      columnFilters,
      pagination,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    // Pagination
    manualPagination: true,
    rowCount: data?.totalResultCount,
    pageCount: data?.totalPageCount,
    onPaginationChange: setPagination,
    // Column filters
    enableFilters: true,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
  })

  return <MantineReactTable table={table} />
}
